import { uploadToS3 } from "@/utils/generalUtils";
import {
  DeleteOutlined,
  InboxOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import {
  UploadFile as AntUploadFile,
  Progress,
  Spin,
  theme,
  Tooltip,
  Upload,
  UploadProps,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import { DesignTemplate } from "api/config/chalice-types";
import { limitedMediaMessage, MAX_SLIDES_FOR_POST } from "fixtures/constants";
import { genericError, UseStateReturn } from "fixtures/globalConstants";
import { useState } from "react";
import {
  ACCEPTED_IMAGE_FORMATS_STR,
  ACCEPTED_VIDEO_FORMATS_STR,
  validateUploadedFile,
} from "utils/validateFileUpload";
import { v4 } from "uuid";

type UploadMediaProps = {
  mediaUrlsState: UseStateReturn<string[]>;
  templateState: UseStateReturn<DesignTemplate | undefined>; // Needed for validation count
  onUploadComplete: (newUrls: string[]) => void;
};

export const UploadMedia = ({
  mediaUrlsState,
  templateState,
  onUploadComplete,
}: UploadMediaProps) => {
  const [mediaUrls] = mediaUrlsState;
  // No need for template directly, just its presence for logic later (passed to onUploadComplete)
  // const [template] = templateState; // Only need mediaUrls for count validation here
  const [uploadingFileList, setUploadingFileList] = useState<AntUploadFile[]>(
    []
  );
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // --- Upload Handlers (Moved from AddMediaModal) ---

  const beforeUpload: UploadProps["beforeUpload"] = (file, fileList) => {
    const isValidFile = validateUploadedFile(file);
    if (!isValidFile) {
      return Upload.LIST_IGNORE;
    }

    const currentUploadCount = uploadingFileList.filter(
      (f) => f.status !== "error"
    ).length;
    const pendingCount = fileList.length;
    const existingCount = mediaUrls.length;

    const fileIndexInBatch = fileList.findIndex((f) => f.uid === file.uid);
    const isOverLimit =
      existingCount + currentUploadCount + fileIndexInBatch + 1 >
      MAX_SLIDES_FOR_POST;

    if (isOverLimit) {
      if (existingCount + currentUploadCount < MAX_SLIDES_FOR_POST) {
        limitedMediaMessage();
      }
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const customRequest: UploadProps["customRequest"] = async ({
    file,
    onSuccess,
    onError,
    onProgress,
  }) => {
    const antdFile = file as AntUploadFile;
    try {
      const extension = antdFile.name.split(".").pop()?.toLowerCase();
      const fileName = `${v4()}.${extension}`;

      const s3Url = await uploadToS3(
        antdFile,
        fileName,
        (progressEvent, _file) => {
          if (onProgress) {
            onProgress(progressEvent);
          }
        }
      );
      if (onSuccess) {
        onSuccess(s3Url, antdFile as unknown as XMLHttpRequest);
      }
    } catch (error) {
      genericError(`Failed to upload ${antdFile.name}`);
      if (onError) {
        onError(error as Error);
      }
    }
  };

  const onChange: UploadProps["onChange"] = ({ file, fileList }) => {
    const nextFileList = fileList.map((f) => ({ ...f }));
    setUploadingFileList(nextFileList);

    const allDone = nextFileList.every(
      (f) => f.status === "done" || f.status === "error"
    );

    if (allDone && nextFileList.length > 0) {
      const successfulFiles = nextFileList.filter((f) => f.status === "done");
      const newUrls = successfulFiles
        .map((f) => f.response as string)
        .filter(Boolean);

      if (newUrls.length > 0) {
        setIsProcessing(true);
        try {
          Promise.resolve(onUploadComplete(newUrls)).finally(() => {
            setIsProcessing(false);
            setUploadingFileList([]);
          });
        } catch (error) {
          genericError("Failed to process uploaded media.");
          setIsProcessing(false);
          setUploadingFileList([]);
        }
      } else {
        setUploadingFileList([]);
      }
    }
  };

  // --- Rendering ---
  return (
    <div className="w-full">
      <Dragger
        className="w-full h-auto p-4"
        beforeUpload={beforeUpload}
        customRequest={customRequest}
        onChange={onChange}
        fileList={uploadingFileList}
        accept={`${ACCEPTED_IMAGE_FORMATS_STR}, ${ACCEPTED_VIDEO_FORMATS_STR}`}
        multiple
        showUploadList={true}
        disabled={isProcessing}
        progress={{
          strokeColor: {
            "0%": "#a78bfa",
            "100%": "#5b21b6",
          },
          strokeWidth: 3,
          format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
        }}
        itemRender={(originNode, file, fileList, actions) => {
          const { status, percent, name } = file;
          const isUploading = status === "uploading";
          const isError = status === "error";
          const { token } = theme.useToken();

          return (
            <div
              className={`ant-upload-list-item flex items-center w-full mb-1 px-2 ${
                isError
                  ? "ant-upload-list-item-error"
                  : `ant-upload-list-item-${status}`
              }`}
              style={{ height: token.controlHeight }}
            >
              <span className="ant-upload-text-icon mr-2">
                <PaperClipOutlined />
              </span>
              <div className="flex-grow overflow-hidden mr-2">
                <Tooltip title={name}>
                  <span className="ant-upload-list-item-name block truncate">
                    {name}
                  </span>
                </Tooltip>
                {isUploading && percent !== undefined && (
                  <Progress
                    percent={percent}
                    size="small"
                    showInfo={false}
                    strokeColor={{ "0%": "#a78bfa", "100%": "#5b21b6" }}
                    className="!mb-0"
                  />
                )}
              </div>
              <span className="ant-upload-list-item-actions ml-auto">
                <Tooltip title="Remove file">
                  <button
                    type="button"
                    className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-action"
                    onClick={actions.remove}
                    aria-label="Remove file"
                  >
                    <DeleteOutlined />
                  </button>
                </Tooltip>
              </span>
            </div>
          );
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag image/video to this area to upload
        </p>
        <p className="ant-upload-hint">
          Up to {MAX_SLIDES_FOR_POST} total slides allowed.
        </p>
      </Dragger>
      {isProcessing && <Spin className="mt-4 flex justify-center" />}
    </div>
  );
};
